import { useEffect } from 'react'
import { useNextStep } from 'nextstepjs'

/** The attribute nextstepjs puts on its tooltip card. */
const CARD_SELECTOR = '[data-name="nextstep-card"]'

/**
 * Longest plausible duration of nextstepjs's own `behavior: 'smooth'` scroll, which it starts when
 * the step changes. Measuring before that settles would clamp against a scroll still in motion.
 */
const SCROLL_SETTLE_MS = 450

/**
 * Keeps the tour's tooltip — and so its Next button — on screen.
 *
 * nextstepjs places the card relative to its target and never checks whether the result is
 * actually visible: its cut-off check compares against the *document* height rather than the
 * viewport, so it never flips the card to the target's other side. On a short window a tall
 * target (the Featured shelf is 467px) pushes the card past the fold with Next below it,
 * leaving no way to advance.
 *
 * `block: 'nearest'` scrolls the least amount needed to reveal the card and does nothing when it
 * is already fully visible, so this is inert on roomy windows and a small correction on tight
 * ones. The card's `scroll-margin` (global.scss) keeps it clear of the sticky nav.
 */
export function useKeepAppTourCardInView(): void {
  const { currentStep, isNextStepVisible } = useNextStep()

  useEffect(() => {
    if (!isNextStepVisible) return undefined

    const timer = setTimeout(() => {
      document.querySelector(CARD_SELECTOR)?.scrollIntoView({ block: 'nearest', behavior: 'auto' })
    }, SCROLL_SETTLE_MS)

    return () => clearTimeout(timer)
  }, [currentStep, isNextStepVisible])
}
